import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Sparkles, Bell, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import JVCard from "@/components/JVCard";
import SaveSearchButton from "@/components/SaveSearchButton";
import { api, formatApiError } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1483683804023-6ccdb62f86ef?w=1920&q=85";

export default function JVPortal() {
    const [jvs, setJvs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [risk, setRisk] = useState("all");
    const [type, setType] = useState("all");

    const fetch = async () => {
        setLoading(true);
        const params = {};
        if (risk !== "all") params.risk_profile = risk;
        if (type !== "all") params.project_type = type;
        try {
            const { data } = await api.get("/jv", { params });
            setJvs(data.items || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [risk, type]);

    const [watchlistEmail, setWatchlistEmail] = useState("");
    const [joining, setJoining] = useState(false);
    const joinWatchlist = async (e) => {
        e.preventDefault();
        setJoining(true);
        try {
            await api.post("/investor-watchlist", { email: watchlistEmail });
            toast.success("You're on the Investor Watchlist. Premium Joint Ventures will hit your inbox first.");
            setWatchlistEmail("");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setJoining(false); }
    };

    const premiumCount = jvs.filter(j => j.is_premium).length;

    return (
        <div data-testid="jv-portal-page">
            <section className="relative min-h-[60vh] flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${HERO_BG}')` }} />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-white w-full">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-[0.3em] text-turquoise font-app font-semibold mb-3">Joint Venture Portal</div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">
                            Vetted Joint Venture deals.<br />
                            <span className="italic text-turquoise">Real Fijian upside.</span>
                        </h1>
                        <p className="font-app text-white/85 max-w-2xl mb-6">
                            Every Joint Venture below has been reviewed and scored by our deal team. Landowners contribute land as equity, investors bring capital, and Bula Real Estate Fiji structures the partnership.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Button asChild size="lg" data-testid="submit-jv-cta" className="bg-coral hover:bg-coral-deep text-white font-app">
                                <Link to="/jv/submit">Submit a Joint Venture proposal <ArrowRight className="ml-2 h-4 w-4" /></Link>
                            </Button>
                            <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-app">
                                <Link to="/how-it-works">How Joint Ventures work</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-card border border-border rounded-xl p-4 -mt-10 relative z-10 shadow-xl flex flex-wrap items-center gap-3">
                    <ShieldCheck className="h-5 w-5 text-fiji-green" />
                    <span className="font-app font-medium text-sm">All deals are platform-vetted.</span>
                    {premiumCount > 0 && (
                        <span className="font-app text-xs text-coral flex items-center gap-1"><Star className="h-3 w-3" /> {premiumCount} Premium featured</span>
                    )}
                    <div className="flex-1" />
                    <Select value={type} onValueChange={setType}>
                        <SelectTrigger data-testid="jv-filter-type" className="w-44"><SelectValue placeholder="Project type" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All project types</SelectItem>
                            <SelectItem value="tourism">Tourism</SelectItem>
                            <SelectItem value="residential">Residential</SelectItem>
                            <SelectItem value="commercial">Commercial</SelectItem>
                            <SelectItem value="subdivision">Subdivision</SelectItem>
                            <SelectItem value="mixed_use">Mixed Use</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={risk} onValueChange={setRisk}>
                        <SelectTrigger data-testid="jv-filter-risk" className="w-36"><SelectValue placeholder="Risk" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All risk</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Investor Watchlist CTA */}
                <Card data-testid="investor-watchlist-cta" className="mt-6 p-6 bg-ocean-green text-white flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
                    <div className="flex items-start gap-3">
                        <Bell className="h-6 w-6 text-turquoise flex-shrink-0 mt-1" />
                        <div>
                            <h3 className="font-display text-xl font-bold">Get Premium Joint Ventures first</h3>
                            <p className="text-sm font-app text-white/85">Join the Investor Watchlist. The moment a Joint Venture is upgraded to Premium, it lands in your inbox.</p>
                        </div>
                    </div>
                    <form onSubmit={joinWatchlist} className="flex gap-2 w-full md:w-auto">
                        <Input data-testid="watchlist-email-input" type="email" required value={watchlistEmail} onChange={(e) => setWatchlistEmail(e.target.value)} placeholder="investor@email.com" className="bg-white/10 border-white/20 text-white placeholder:text-white/60 md:w-64" />
                        <Button data-testid="join-watchlist-btn" type="submit" disabled={joining} className="bg-coral hover:bg-coral-deep text-white font-app whitespace-nowrap">{joining ? "..." : "Join Watchlist"}</Button>
                    </form>
                </Card>

                <div className="mt-10">
                    <div className="flex items-center justify-end mb-3">
                        <SaveSearchButton
                            kind="jv"
                            filters={{
                                ...(risk !== "all" ? { risk_profile: risk } : {}),
                                ...(type !== "all" ? { project_type: type } : {}),
                            }}
                            defaultName={[
                                type !== "all" ? type : null,
                                risk !== "all" ? `${risk} risk` : null,
                            ].filter(Boolean).join(" · ") || "All Joint Ventures"}
                        />
                    </div>
                    {loading && <div className="text-muted-foreground font-app">Loading deals…</div>}
                    {!loading && jvs.length === 0 && (
                        <div className="text-center py-20">
                            <Sparkles className="h-10 w-10 mx-auto text-coral mb-3" />
                            <h3 className="font-display text-2xl font-semibold mb-2">No approved Joint Ventures match your filters</h3>
                            <p className="text-muted-foreground font-app">Try adjusting filters or check back soon — new deals are vetted weekly.</p>
                        </div>
                    )}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {jvs.map((jv) => <JVCard key={jv.id} jv={jv} />)}
                    </div>
                </div>
            </section>
        </div>
    );
}
